import { api } from "./base-config";
import { Contact, ContactType } from "model/contact.model";
import moment from "moment";
import { DateMaskEnum } from "common/enums/DateMaskEnum";
import { StatusTypeEnum } from "common/enums/StatusTypeEnum";
import { ConfidentialityTypeEnum } from "common/enums/ConfidentialityTypeEnum";
import { ContactTypeEnum } from "common/enums/ContactTypeEnum";
import { ActionDate } from "model/action-date.model";

/** ---- API ---- */
export const createEmptyContact = (
  patientId: number,
  caregiverId: number,
  contactType: ContactTypeEnum
): Promise<ContactType> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/contacts`,
      emptyContact(contactType)
    )
    .then(response => mapToContact(response.data));
};

export const updateContact = (
  patientId: number,
  caregiverId: number,
  contact: ContactType
): Promise<ContactType> => {
  return api
    .put(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/contacts/${contact.id}`,
      mapToContactDTO(contact)
    )
    .then(response => mapToContact(response.data));
};

export const findAllContacts = (
  patientId: number,
  caregiverId: number
): Promise<Array<ContactType>> => {
  return api
    .get(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/contacts/all`
    )
    .then(response => {
      return Promise.resolve(mapToContactEntries(response.data));
    });
};

/** ---- Mapper ---- */
const mapToContactDTO = (contact: ContactType): ContactDTO => {
  return Object.assign({} as ContactDTO, contact.dto, {
    id: contact.id,
    tarificationSessionId: contact.tarificationSessionId
  });
};

const mapToContactEntries = (response: any): Array<ContactType> => {
  const entrySet = new Set<ContactType>();
  response.forEach((entry: ContactDTO) => {
    entrySet.add(mapToContact(entry));
  });
  return Array.from<ContactType>(entrySet);
};

const mapToContact = (dto: ContactDTO): ContactType => {
  return new Contact({
    id: dto.id,
    tarificationSessionId: dto.tarificationSessionId || "",
    actionDate: new ActionDate(
      moment(dto.actionDate).toDate(),
      dto.actionDateMask
    ),
    dto: dto
  });
};

const emptyContact = (contactType: ContactTypeEnum): ContactDTO => {
  return {
    "@type": "Contact",
    id: -1,
    type: contactType,
    actionDate: moment().toDate(),
    actionDateMask: DateMaskEnum.DDMMYYYYHHMM,
    status: StatusTypeEnum.ACTIVE,
    confidentiality: ConfidentialityTypeEnum.C2,
    tarificationSessionId: ""
  };
};

/** ---- DTO ---- */
interface ContactDTO {
  readonly "@type": "Contact";
  readonly id: number;
  readonly type: ContactTypeEnum;
  readonly actionDate: Date;
  readonly actionDateMask: DateMaskEnum;
  readonly status: StatusTypeEnum;
  readonly confidentiality: ConfidentialityTypeEnum;
  readonly tarificationSessionId?: string;
}
